import type {MbscCalendarEvent, MbscResource} from "@mobiscroll/react";
import type {AgendaV3} from "crqlar-react-common/dist/models/agenda/agenda.model";
import type {Reservation} from "crqlar-react-common/dist/models/reservation/reservation.model";
import type {Service} from "crqlar-react-common/dist/models/service/service.model";
import {
    useCalculatedAgenda,
    useCalculatedAgendaAvailabilities,
} from "crqlar-react-common/dist/utils/hooks/agenda/useAgendaData";
import {useServiceReservations} from "crqlar-react-common/dist/utils/hooks/reservations/useReservationsSchedule";
import {useServices} from "crqlar-react-common/dist/utils/hooks/services/useServices";
import {useMemo} from "react";
import type {CalendarViewSpan} from "../rail/CalendarViewPicker";

interface UseWeeklyPlannerCalendarDataProps {
    agenda?: AgendaV3;
    selectedServiceIds?: readonly string[];
    span: CalendarViewSpan;
    startDate: Date;
    endDate: Date;
}

export function useWeeklyPlannerCalendarData({
    agenda,
    selectedServiceIds,
    span,
    startDate,
    endDate,
}: UseWeeklyPlannerCalendarDataProps) {
    const selectedServiceIdSet = useMemo(
        () => selectedServiceIds ? new Set(selectedServiceIds) : undefined,
        [selectedServiceIds],
    );
    const serviceIds = useMemo(() => (
        (agenda?.serviceIds ?? []).filter(serviceId => (
            !selectedServiceIdSet || selectedServiceIdSet.has(serviceId)
        ))
    ), [agenda?.serviceIds, selectedServiceIdSet]);
    const serviceIdSet = useMemo(() => new Set(serviceIds), [serviceIds]);
    const loadedServices = useServices(serviceIds);
    const services = useMemo(
        () => loadedServices.filter(service => service.id && serviceIdSet.has(service.id)),
        [loadedServices, serviceIdSet],
    );
    const calculatedAgendaQuery = useCalculatedAgenda(agenda?.id, startDate, endDate);

    const resourceIds = useMemo(() => {
        const ids = calculatedAgendaQuery.data?.items
            .filter(item => item.service.id && serviceIdSet.has(item.service.id))
            .flatMap(item => item.service.resourceIds ?? [])
            .filter(Boolean) as string[] | undefined;

        return Array.from(new Set(ids ?? []));
    }, [calculatedAgendaQuery.data, serviceIdSet]);

    const availabilityQuery = useCalculatedAgendaAvailabilities(
        resourceIds,
        startDate,
        endDate,
    );
    const reservationsQuery = useServiceReservations(
        serviceIds,
        span,
        startDate,
        endDate,
        agenda?.id,
    );
    const resources = useMemo<MbscResource[]>(() => services.reduce<MbscResource[]>((result, service) => {
        if (!service.id) return result;

        result.push({
            id: service.id,
            name: service.name || "",
            color: service.color?.background,
        });
        return result;
    }, []), [services]);

    const events = useMemo<MbscCalendarEvent[]>(() => {
        const calendarEvents: MbscCalendarEvent[] = [];
        const reservations = reservationsQuery.data ?? [];
        const rangeStart = startDate.getTime();
        const rangeEnd = endDate.getTime();

        services.forEach((service: Service) => {
            if (!service.id || !service.availabilityBindings?.length) return;

            const availabilityIds = service.availabilityBindings.flatMap(
                binding => binding.availabilityIds,
            );
            const serviceAvailabilities = availabilityQuery.data?.filter(availability => (
                availability.availabilityId
                && availabilityIds.includes(availability.availabilityId)
            ));
            if (!serviceAvailabilities?.length) return;

            const serviceReservations = reservations.filter(
                reservation => reservation.serviceId === service.id,
            );
            const fallbackColor = serviceAvailabilities[0]?.color;

            serviceAvailabilities.forEach(availability => {
                if (!availability.startTime || !availability.endTime) return;

                const start = new Date(availability.startTime);
                const end = new Date(availability.endTime);
                if (start.getTime() < rangeStart || start.getTime() > rangeEnd) return;

                const occurrenceReservations = serviceReservations.filter((reservation: Reservation) => {
                    if (!reservation.expectedStartDate) return false;

                    const reservationStart = new Date(reservation.expectedStartDate).getTime();
                    return reservationStart >= start.getTime() && reservationStart < end.getTime();
                });
                const profileIds = occurrenceReservations.flatMap(
                    reservation => reservation.profileIds ?? [],
                );
                const bookedCount = occurrenceReservations.reduce((total, reservation) => (
                    total + (reservation.guestCounts?.reduce(
                        (guestTotal, guestCount) => guestTotal + (guestCount.count || 0),
                        0,
                    ) ?? 0)
                ), 0);

                calendarEvents.push({
                    id: `${service.id}-${availability.availabilityId}-${start.getTime()}`,
                    start,
                    end,
                    title: availability.name,
                    resource: service.id,
                    color: availability.color ?? fallbackColor,
                    serviceId: service.id,
                    availabilityId: availability.availabilityId,
                    availability,
                    reservations: occurrenceReservations,
                    profileIds,
                    bookedCount,
                    maxCapacity: availability.capacityOverride,
                    type: availability.type,
                });
            });
        });

        return calendarEvents;
    }, [availabilityQuery.data, endDate, reservationsQuery.data, services, startDate]);

    const refetch = async () => {
        const requests: Promise<unknown>[] = [calculatedAgendaQuery.refetch()];
        if (resourceIds.length > 0) requests.push(availabilityQuery.refetch());
        if (serviceIds.length > 0) requests.push(reservationsQuery.refetch());

        await Promise.all(requests);
    };

    return {
        services,
        resources,
        events,
        isLoading: calculatedAgendaQuery.isLoading
            || reservationsQuery.isLoading
            || (resourceIds.length > 0 && availabilityQuery.isLoading),
        isError: calculatedAgendaQuery.isError
            || availabilityQuery.isError
            || reservationsQuery.isError,
        refetch,
    };
}
